// src/app/core/posthog.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import posthog from 'posthog-js';
import {
  BehaviorSubject,
  Observable,
  firstValueFrom,
  map,
  switchMap,
  tap,
} from 'rxjs';
import {
  PendingSurvey,
  Survey,
  SurveyConfig,
  SurveyResponseValue,
} from '../dialogs/survey-popover/survey.types';
import type { SurveyServiceInterface } from './survey-service-interface';
import { HttpService } from './http/http.service';

@Injectable({ providedIn: 'root' })
export class PosthogService implements SurveyServiceInterface {
  private _activeSurvey$ = new BehaviorSubject<PendingSurvey | null>(null);
  activeSurvey$: Observable<PendingSurvey | null> =
    this._activeSurvey$.asObservable();

  private _initPromise: Promise<void> | null = null;

  constructor(
    private ngZone: NgZone,
    private httpClient: HttpClient,
    private httpService: HttpService,
  ) {}

  /**
   * Fetches the PostHog provider key/host from the (authenticated) backend
   * and initializes posthog-js. Idempotent — repeat callers share the same
   * in-flight promise. Rejects and clears the cached promise on failure so
   * the next call retries.
   *
   * Must be invoked only after the user is authenticated (e.g. from the
   * login flow or a session-restore branch); the config endpoint requires
   * a valid session.
   */
  ensureInitialized(): Promise<void> {
    if (this._initPromise) return this._initPromise;

    this._initPromise = new Promise<void>((resolve, reject) => {
      this.ngZone.runOutsideAngular(async () => {
        try {
          const surveyConfig$ = <
            Observable<{ status: string; title: string; data: SurveyConfig }>
          >this.httpService.get('/course-user/survey-config');
          const surveyConfig = await firstValueFrom(surveyConfig$);
          posthog.init(surveyConfig.data.provider_key, {
            api_host: surveyConfig.data.provider_host,
            defaults: '2026-01-30',
            disable_surveys_automatic_display: true,
            persistence: 'memory',
          });
          resolve();
        } catch (err) {
          this._initPromise = null;
          reject(err);
        }
      });
    });

    return this._initPromise;
  }

  /**
   * Generic entry point. Pushes a `PendingSurvey` (survey definition + extra
   * properties to attach to its events) onto `activeSurvey$`. The popover
   * component picks it up and renders. Looks up the survey by id via
   * `posthog.getActiveMatchingSurveys`, which waits for the survey list to
   * finish loading before invoking its callback.
   *
   * When `options.ignoreTargeting` is true, the survey is looked up from the
   * full project survey list via `posthog.getSurveys()` — useful for testing,
   * since `getActiveMatchingSurveys` filters out surveys the user already
   * responded to (e.g. `schedule: 'once'`).
   */
  showSurvey(
    surveyId: string,
    superProps: Record<string, unknown> = {},
    options: { ignoreTargeting?: boolean } = {},
  ): void {
    this.ensureInitialized()
      .then(() => {
        if (options.ignoreTargeting) {
          posthog.getSurveys((all) => {
            const survey = (all as unknown as Survey[]).find(
              (s) => s.id === surveyId,
            );
            if (!survey) {
              console.warn('[PostHog] Survey not found in project:', surveyId);
              return;
            }
            this._activeSurvey$.next({ survey, superProps });
          });
          return;
        }

        posthog.getActiveMatchingSurveys((matching) => {
          const survey = (matching as unknown as Survey[]).find(
            (s) => s.id === surveyId,
          );
          if (!survey) {
            console.warn(
              '[PostHog] Survey not active/matching for this user:',
              surveyId,
            );
            return;
          }
          this._activeSurvey$.next({ survey, superProps });
        });
      })
      .catch((err) => {
        console.warn('[PostHog] init failed; survey not shown:', err);
      });
  }

  /** Fires PostHog's `survey shown` event once when the popover renders. */
  markSurveyShown(pending: PendingSurvey): void {
    posthog.capture('survey shown', {
      $survey_id: pending.survey.id,
      ...pending.superProps,
    });
  }

  /**
   * Submit the collected responses for a pending survey. Builds the
   * `$survey_response` / `$survey_response_<n>` payload PostHog expects and
   * dismisses the popover.
   */
  submitSurveyResponse(
    pending: PendingSurvey,
    responses: Record<string, SurveyResponseValue>,
  ): void {
    const payload: Record<string, unknown> = {
      $survey_id: pending.survey.id,
      $survey_questions: pending.survey.questions.map((q) => ({
        id: q.id,
        question: q.question,
      })),
      ...pending.superProps,
    };
    pending.survey.questions.forEach((q, i) => {
      const key = i === 0 ? '$survey_response' : `$survey_response_${i}`;
      payload[key] = responses[q.id] ?? null;
    });
    posthog.capture('survey sent', payload);
    this._activeSurvey$.next(null);
  }

  /** Fires PostHog's `survey dismissed` event and closes the popover. */
  dismissSurvey(pending: PendingSurvey): void {
    posthog.capture('survey dismissed', {
      $survey_id: pending.survey.id,
      ...pending.superProps,
    });
    this._activeSurvey$.next(null);
  }

  /**
   * Convenience trigger for the CSAT survey on the course-overview page.
   * Resolves the user's federal state from their ZIP via openplzapi and then
   * delegates to the generic `showSurvey`.
   *
   * `ignoreTargeting` forwards to `showSurvey` and is meant for local testing.
   */
  validateDisplaySurvey(
    survey_type: string,
    page_type: string,
    id: string,
    urlTree: string,
    zipCode: string,
    ignoreTargeting = false,
  ): Observable<unknown> {
    const isValid$ = this.httpService.post(
      `/course-user/survey-valid/${survey_type}`,
      {
        type: page_type,
        id: id,
        urlTree: urlTree,
      },
    );

    const request$ = (courseTitle: string, survey_id: string) =>
      this.httpClient
        .get(`https://openplzapi.org/de/Localities?postalCode=${zipCode}`)
        .pipe(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          tap((data: any) => {
            const federalState = data?.[0]?.federalState?.name;
            this.showSurvey(
              survey_id,
              {
                course_id: id,
                course_title: courseTitle,
                federal_state: federalState,
                source: 'WEB',
              },
              { ignoreTargeting },
            );
          }),
        );

    return isValid$.pipe(
      map((resp: any) => resp.data),
      switchMap((data: any) => {
        if (data.valid) return request$(data.title, data.survey_id);
        else return new BehaviorSubject(false);
      }),
    );
  }
}
